/**
 * emailGate — pure decision helper for "does this golfer need to add /
 * verify an email before we can send them a receipt or notification?"
 *
 * Phone-only golfers get a synthetic `<phone>@phone.golfauth.local`
 * email at signup so Supabase Auth has a value to hash against. Nothing
 * routes to that address — every email trigger (welcome, purchase,
 * transfer, quote, receipt) black-holes for those users until they
 * add a real email + verify the Supabase link. This helper is the
 * single source of truth for the surrounding UI / server gates.
 *
 * The two consumers today:
 *   * `<GolferEmailRequiredModal>` uses `needsEmailForAction(customer)`
 *     to decide whether to show the modal + which reason copy to render.
 *   * The 4 edge functions that send golfer-facing emails
 *     (send-purchase-order, send-payment-confirmed-email,
 *     send-order-confirmation, send-transfer-notification-email) run
 *     the same check server-side as a belt-and-braces safety net so a
 *     bug in the client can't silently swallow deliveries.
 *
 * The server-side twin is `supabase/functions/_shared/requireVerifiedEmail.ts`
 * — keep them in shape sync.
 */

import { isPlaceholderEmail } from './golferEmail';

export type EmailGateReason = 'placeholder' | 'unverified';

export interface EmailGateSubject {
  email: string | null | undefined;
  email_verified?: boolean | null | undefined;
}

export interface EmailGateResult {
  /** True when the current email cannot receive real mail. Callers
   *  should block the action + prompt for a real address. */
  block: boolean;
  /** Non-null when `block=true`. Drives the modal copy:
   *  - `placeholder` — synthetic `@phone.golfauth.local` — no email at all.
   *  - `unverified`  — real email typed but not yet clicked in inbox. */
  reason: EmailGateReason | null;
}

export function needsEmailForAction(
  subject: EmailGateSubject | null | undefined,
): EmailGateResult {
  if (!subject) {
    // No customer row means this session isn't a golfer — either a
    // signed-in professional (customer is always null in the pro-side
    // authStore branch) or a not-yet-hydrated state. Either way this
    // gate doesn't apply: pros have their own professionals.email
    // verification flow, and blocking them on /pro/checkout would
    // leave orders unfinalized (Bugbot round-2 HIGH on #359). Callers
    // that require golfer-only enforcement should still check for the
    // customer row before invoking downstream logic.
    return { block: false, reason: null };
  }
  if (isPlaceholderEmail(subject.email)) {
    return { block: true, reason: 'placeholder' };
  }
  if (subject.email_verified !== true) {
    return { block: true, reason: 'unverified' };
  }
  return { block: false, reason: null };
}
